import { orgAddressFragment } from 'apollo/fragments';
import {
    orgNotificationFragment,
    orgEmailNotificationFragment,
    externalGeneralNotificationsFragment,
    externalEmailNotificationsFragment,
    localDentistEmailNotificationsFragment,
    listViewProfileFragment,
} from 'apollo/fragments/organization';
import { gql } from 'graphql.macro';

export const updateLabNotificationsTemplate = gql`
    mutation updateLabNotifications($labNotificationsSettings: LabNotificationsInput!) {
        updateLabNotifications(labNotificationsSettings: $labNotificationsSettings) {
            generalNotifications {
                ...orgNotificationFields
            }
            emailNotifications {
                ...orgEmailNotificationFields
            }
            externalGeneralNotifications {
                ...externalGeneralNotificationsFields
            }
            externalEmailNotifications {
                ...externalEmailNotificationsFields
            }
            localDentistEmailNotifications {
                ...localDentistEmailNotificationsFields
            }
            notificationListDefaults {
                showOnlyUnread
                showNotDone
            }
        }
    }
    ${orgNotificationFragment}
    ${orgEmailNotificationFragment}
    ${externalGeneralNotificationsFragment}
    ${externalEmailNotificationsFragment}
    ${localDentistEmailNotificationsFragment}
`;
export const updateLabContactAndAvailabilityTemplate = gql`
    mutation updateLabContactAndAvailability($labContactAndAvailabilityData: LabContactAndAvailabilityInput) {
        updateLabContactAndAvailability(labContactAndAvailabilityData: $labContactAndAvailabilityData) {
            unavailableLabDates
            holidayDays
            openingHours {
                active
                day
                time {
                    from
                    to
                }
            }
        }
    }
`;
export const updateGeneralInformationTemplate = gql`
    mutation updateGeneralInformation($generalInformationData: GeneralInformationInput!) {
        updateGeneralInformation(GeneralInformationData: $generalInformationData) {
            name
            publishLab
            sourceCurrency
            contact {
                address {
                    street
                    postalCode
                    city
                    country
                    deliveryInstructions
                }
                communication {
                    email
                    phone
                    site
                }
            }
            currentJobs {
                active
                deliveryDays
                name
            }
            export {
                additionalDeliveryDays
                enable
            }
        }
    }
`;

export const modifyOrganizationLocaleFormatTemplate = gql`
    mutation modifyOrganizationLocaleFormat($input: LocaleFormatInput!) {
        modifyOrganizationLocaleFormat(input: $input) {
            _id
            localeFormat {
                language
                date
                number
            }
        }
    }
`;

export const updateLabProfileTemplate = gql`
    mutation updateLabProfile($labProfileSettings: LabProfileInput) {
        updateLabProfile(labProfileSettings: $labProfileSettings) {
            hexCode
            logoImage
            longDescription
            shortDescription
            profileTexts {
                language
                shortDescription
                longDescription
            }
            staffImage
            textCode
        }
    }
`;

// DD-3662: how the lab presents itself to other labs in the supplier browser.
export const updateSupplierProfileTemplate = gql`
    mutation updateSupplierProfile($supplierProfile: SupplierProfileInput!) {
        updateSupplierProfile(supplierProfile: $supplierProfile) {
            _id
            supplierProfile {
                visible
                services
                otherService
                texts {
                    language
                    description
                }
            }
        }
    }
`;
export const toggleTransportSettingTemplate = gql`
    mutation toggleTransportSetting($isTransportEnable: Boolean) {
        toggleTransportSetting(isTransportEnable: $isTransportEnable) {
            isTransportEnable
        }
    }
`;

export const toggleApiEnabledTemplate = gql`
    mutation toggleApiEnabled($isApiEnabled: Boolean!) {
        toggleApiEnabled(isApiEnabled: $isApiEnabled) {
            _id
            isApiEnabled
        }
    }
`;

export const updateOrgInvoicingSettingTemplate = gql`
    mutation updateOrgInvoicingSetting($OrgInvoicingSettingData: OrgInvoicingSettingInput) {
        updateOrgInvoicingSetting(OrgInvoicingSettingData: $OrgInvoicingSettingData) {
            invoicing {
                vatNo
                costCenter
                invoiceAddress {
                    ...orgAddressFields
                }
                communication {
                    email
                    phone
                    site
                }
                paymentDetails {
                    postgiro
                    bankgiro
                    bicSwift
                    iban
                }
                defaultSetting {
                    invoiceType
                    invoiceDelivery
                    invoicePeriod
                    paymentTerms
                }
                configuration {
                    invoiceNumberType
                    currentStartingNumber
                }
                invoiceText
            }
        }
    }
    ${orgAddressFragment}
`;

export const updateWaybillPrintFormatTemplate = gql`
    mutation updateWaybillPrintFormat($printFormat: String!) {
        updateWaybillPrintFormat(printFormat: $printFormat)
    }
`;

export const createWaybillPackageSizeTemplate = gql`
    mutation createWaybillPackageSize($packageSize: WaybillPackageSizeInput!) {
        createWaybillPackageSize(packageSize: $packageSize) {
            _id
            name
            width
            height
            length
        }
    }
`;

export const deleteWaybillPackageSizeTemplate = gql`
    mutation deleteWaybillPackageSize($packageSizeId: MongoDbObjectId!) {
        deleteWaybillPackageSize(packageSizeId: $packageSizeId)
    }
`;

export const connectSimpleClinicToLabTemplate = gql`
    mutation connectSimpleClinicToLab($labId: MongoDbObjectId!) {
        connectSimpleClinicToLab(labId: $labId)
    }
`;

export const disconnectSimpleClinicToLabTemplate = gql`
    mutation disconnectSimpleClinicToLab($labId: MongoDbObjectId!) {
        disconnectSimpleClinicToLab(labId: $labId)
    }
`;

export const connectLabToClinicTemplate = gql`
    mutation ConnectLabToClinic($lab: MongoDbObjectId!) {
        connectLabToClinic(lab: $lab)
    }
`;

export const enableDisableConnectedLabTemplate = gql`
    mutation updateConnectedLabStatus($labId: MongoDbObjectId!, $isDisabled: Boolean!) {
        updateConnectedLabStatus(labId: $labId, isDisabled: $isDisabled)
    }
`;

export const requireExceptionConnectedLabTemplate = gql`
    mutation UpdateConnectedLabRequireException($labId: MongoDbObjectId!, $isRequireException: Boolean!) {
        updateConnectedLabRequireException(labId: $labId, isRequireException: $isRequireException)
    }
`;

export const toggleTioTandvardEnabledTemplate = gql`
    mutation toggleTioTandvardEnabled($labId: MongoDbObjectId!, $isTioTandvardEnabled: Boolean!) {
        toggleTioTandvardEnabled(labId: $labId, isTioTandvardEnabled: $isTioTandvardEnabled)
    }
`;

export const updateConnectedLabCurrencyTemplate = gql`
    mutation updateConnectedLabCurrency($labId: MongoDbObjectId!, $currency: String!) {
        updateConnectedLabCurrency(labId: $labId, currency: $currency)
    }
`;

export const updateMaterialPriceDiffTemplate = gql`
    mutation updateConnectedLabMaterialPriceDifference(
        $labId: MongoDbObjectId!
        $materialGroupId: MongoDbObjectId!
        $actionSetId: MongoDbObjectId!
        $materials: [MaterialsInput!]!
    ) {
        updateConnectedLabMaterialPriceDifference(
            labId: $labId
            materialGroupId: $materialGroupId
            actionSetId: $actionSetId
            materials: $materials
        )
    }
`;

export const updatePriceListTemplate = gql`
    mutation updateConnectedLabPriceList(
        $labId: MongoDbObjectId!
        $actionSetId: MongoDbObjectId!
        $categoryId: MongoDbObjectId!
        $actions: [ConnectedLabActionInput!]!
        $rank: Int!
    ) {
        updateConnectedLabPriceList(
            labId: $labId
            actionSetId: $actionSetId
            categoryId: $categoryId
            actions: $actions
            rank: $rank
        )
    }
`;

export const deletePriceListTemplate = gql`
    mutation DeleteConnectedLabPriceList(
        $labId: MongoDbObjectId!
        $actionSetId: MongoDbObjectId!
        $categoryId: MongoDbObjectId!
    ) {
        deleteConnectedLabPriceList(labId: $labId, actionSetId: $actionSetId, categoryId: $categoryId)
    }
`;

export const saveMachineTemplate = gql`
    mutation SaveMachine($machine: MachineInput!) {
        saveMachine(machine: $machine)
    }
`;

export const updateMachineTemplate = gql`
    mutation UpdateMachine($machine: MachineInput!) {
        updateMachine(machine: $machine)
    }
`;

export const deleteMachineTemplate = gql`
    mutation DeleteMachine($machineId: MongoDbObjectId!) {
        deleteMachine(machineId: $machineId)
    }
`;

export const savePauseReasonsTemplate = gql`
    mutation SavePauseReasons($reasons: [String!]!) {
        savePauseReasons(reasons: $reasons)
    }
`;

export const resetPauseReasonsTemplate = gql`
    mutation ResetPauseReasons {
        resetPauseReasons
    }
`;

export const saveDepartmentTemplate = gql`
    mutation SaveDepartment($department: DepartmentInput!) {
        saveDepartment(department: $department)
    }
`;

export const updateDepartmentTemplate = gql`
    mutation UpdateDepartment($department: DepartmentInput!) {
        updateDepartment(department: $department)
    }
`;

export const deleteDepartmentTemplate = gql`
    mutation DeleteDepartment($departmentId: MongoDbObjectId!) {
        deleteDepartment(departmentId: $departmentId)
    }
`;

export const reorderDepartmentsTemplate = gql`
    mutation ReorderDepartments($departmentIds: [MongoDbObjectId!]!) {
        reorderDepartments(departmentIds: $departmentIds)
    }
`;

export const saveDocumentBundleTemplate = gql`
    mutation SaveDocumentBundle($documentBundle: DocumentBundleInput!) {
        saveDocumentBundle(documentBundle: $documentBundle)
    }
`;

export const updateDocumentBundleTemplate = gql`
    mutation UpdateDocumentBundle($documentBundle: DocumentBundleInput!) {
        updateDocumentBundle(documentBundle: $documentBundle)
    }
`;

export const deleteDocumentBundleTemplate = gql`
    mutation DeleteDocumentBundle($documentBundleId: MongoDbObjectId!) {
        deleteDocumentBundle(documentBundleId: $documentBundleId)
    }
`;

export const saveProductionPlanningTemplateTemplate = gql`
    mutation SaveProductionPlanningTemplate($template: ProductionPlanningTemplateInput!) {
        saveProductionPlanningTemplate(template: $template)
    }
`;

export const updateProductionPlanningTemplateTemplate = gql`
    mutation UpdateProductionPlanningTemplate($template: ProductionPlanningTemplateInput!) {
        updateProductionPlanningTemplate(template: $template)
    }
`;

export const deleteProductionPlanningTemplateTemplate = gql`
    mutation DeleteProductionPlanningTemplate($templateId: MongoDbObjectId!) {
        deleteProductionPlanningTemplate(templateId: $templateId)
    }
`;

export const saveProductionValueTemplateTemplate = gql`
    mutation SaveProductionValueTemplate($template: ProductionValueTemplateInput!) {
        saveProductionValueTemplate(template: $template)
    }
`;

export const updateProductionValueTemplateTemplate = gql`
    mutation UpdateProductionValueTemplate($template: ProductionValueTemplateInput!) {
        updateProductionValueTemplate(template: $template)
    }
`;

export const deleteProductionValueTemplateTemplate = gql`
    mutation DeleteProductionValueTemplate($templateId: MongoDbObjectId!) {
        deleteProductionValueTemplate(templateId: $templateId)
    }
`;

export const addProductionValueStepTemplate = gql`
    mutation AddProductionValueStep($step: ProductionValueStepConfigInput!) {
        addProductionValueStep(step: $step)
    }
`;

export const updateProductionValueStepTemplate = gql`
    mutation UpdateProductionValueStep($stepId: MongoDbObjectId!, $step: ProductionValueStepConfigInput!) {
        updateProductionValueStep(stepId: $stepId, step: $step)
    }
`;

export const deleteProductionValueStepTemplate = gql`
    mutation DeleteProductionValueStep($stepId: MongoDbObjectId!) {
        deleteProductionValueStep(stepId: $stepId)
    }
`;

export const saveListViewProfileTemplate = gql`
    mutation SaveListViewProfile($profile: ListViewProfileInput!) {
        saveListViewProfile(profile: $profile) {
            ...listViewProfileFields
        }
    }
    ${listViewProfileFragment}
`;

export const deleteListViewProfileTemplate = gql`
    mutation DeleteListViewProfile($profileId: MongoDbObjectId!) {
        deleteListViewProfile(profileId: $profileId)
    }
`;

export const setDefaultOrderListProfileTemplate = gql`
    mutation SetDefaultOrderListProfile($scope: OrderListScope!, $profileId: MongoDbObjectId) {
        setDefaultOrderListProfile(scope: $scope, profileId: $profileId)
    }
`;
